/*
  Recently viewed products.

  Product pages remember the product id in localStorage (most recent first).
  <recently-viewed> then asks the search page for those products through the
  Section Rendering API, so titles, prices and availability always come from
  the server, and shows the returned cards in the order they were viewed.
  Nothing is shown when storage is unavailable or the list is empty.
*/
(function () {
  const Theme = window.Theme;
  if (!Theme) return;

  const STORAGE_KEY = 'theme:recently-viewed';
  const MAX_STORED = 12;

  function storedIds() {
    const ids = Theme.storageGet(STORAGE_KEY);
    if (!Array.isArray(ids)) return [];
    return ids.map(Number).filter((id) => Number.isInteger(id) && id > 0);
  }

  /* The product page marks its root with the id of the product on show. */
  function currentProductId() {
    const root = document.querySelector('[data-recently-viewed-id]');
    const id = root ? Number(root.dataset.recentlyViewedId) : 0;
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  function record() {
    const id = currentProductId();
    if (!id) return;
    const ids = [id].concat(storedIds().filter((stored) => stored !== id)).slice(0, MAX_STORED);
    Theme.storageSet(STORAGE_KEY, ids);
  }

  record();

  if (customElements.get('recently-viewed')) return;

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this.loaded || this.observer) return;

      const current = currentProductId();
      const limit = Number(this.dataset.limit) || 4;
      this.ids = storedIds()
        .filter((id) => id !== current)
        .slice(0, limit);

      if (!this.ids.length || !this.dataset.sectionId) {
        this.hidden = true;
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.stopObserving();
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.stopObserving();
    }

    stopObserving() {
      if (!this.observer) return;
      this.observer.disconnect();
      this.observer = null;
    }

    async load() {
      this.loaded = true;
      const ids = this.ids;
      const query = ids.map((id) => `id:${id}`).join(' OR ');
      const url = `${Theme.routes.search}?q=${encodeURIComponent(query)}&type=product`;

      try {
        const html = await Theme.fetchSection(url, this.dataset.sectionId);
        const page = new DOMParser().parseFromString(html, 'text/html');
        const source = this.id ? page.getElementById(this.id) : page.querySelector('recently-viewed');
        const grid = source ? source.querySelector('[data-recently-viewed-grid]') : null;

        /* Keep only products that were asked for, most recently viewed first */
        const items = grid
          ? Array.from(grid.children)
              .filter((item) => ids.includes(Number(item.dataset.productId)))
              .sort((a, b) => ids.indexOf(Number(a.dataset.productId)) - ids.indexOf(Number(b.dataset.productId)))
          : [];

        if (!items.length) {
          this.hidden = true;
          return;
        }

        grid.replaceChildren(...items);
        this.replaceChildren(...source.childNodes);
      } catch (error) {
        /* the section is optional: on failure it stays out of the page */
        this.hidden = true;
      }
    }
  }

  customElements.define('recently-viewed', RecentlyViewed);
})();
